import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LoginService } from '../../services/login-service';
import { HttpService } from '../utility/http-service';

export interface RecentlyViewedItem {
  label: string;    // "FracX Schedules", "Acme Corp", "Dashboards"
  url: string;      // "/schedules", "/client/abc123"
  icon: string;     // Bootstrap Icons name without "bi-" prefix
  type: string;     // "module" | "admin" | "client" | "record"
  colorKey: string; // "blue" | "green" | "teal" | "amber" | "slate" | "purple" | "red"
  iconUrl?: string; // Base64 data URL for client logos (optional)
  subtitle?: string; // "Dashboard", "Client" — helps distinguish items with the same client name
  timestamp: number;
}

interface RouteEntry {
  pattern: RegExp;
  item: Omit<RecentlyViewedItem, 'timestamp'>;
}

const STATIC_ROUTES: RouteEntry[] = [
  { pattern: /^\/dashboards/,          item: { label: 'Dashboards',      url: '/dashboards',          icon: 'speedometer2',    type: 'module', colorKey: 'blue'   } },
  { pattern: /^\/schedules/,           item: { label: 'FracX Schedules', url: '/schedules',            icon: 'calendar3',       type: 'module', colorKey: 'green'  } },
  { pattern: /^\/watersharing/,        item: { label: 'AquaTrade',       url: '/watersharing',         icon: 'droplet-half',    type: 'module', colorKey: 'teal'   } },
  { pattern: /^\/aquatrade/,           item: { label: 'AquaTrade',       url: '/watersharing',         icon: 'droplet-half',    type: 'module', colorKey: 'teal'   } },
  { pattern: /^\/user-management/,     item: { label: 'User Management', url: '/user-management',      icon: 'people',          type: 'admin',  colorKey: 'slate'  } },
  { pattern: /^\/pending-users/,       item: { label: 'Pending Users',   url: '/pending-users',        icon: 'hourglass-split', type: 'admin',  colorKey: 'red'    } },
  { pattern: /^\/client$/,             item: { label: 'Clients',         url: '/client',               icon: 'building',        type: 'module', colorKey: 'slate'  } },
  { pattern: /^\/record-viewer/,       item: { label: 'Record Viewer',   url: '/record-viewer',        icon: 'file-text',       type: 'record', colorKey: 'purple' } },
  { pattern: /^\/prod-data$/,          item: { label: 'Production Data', url: '/prod-data',            icon: 'database',        type: 'admin',  colorKey: 'amber'  } },
];

@Injectable({ providedIn: 'root' })
export class RecentlyViewedService {

  private readonly MAX_ITEMS = 8;
  private readonly KEY_PREFIX = 'wc-recent:';

  private _items$ = new BehaviorSubject<RecentlyViewedItem[]>([]);
  readonly items$ = this._items$.asObservable();

  constructor(private loginService: LoginService, private router: Router, private http: HttpService) {
    // Listen to every completed navigation and auto-record known routes
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(e => {
        const item = this.mapUrlToItem((e as NavigationEnd).urlAfterRedirects);
        if (item) this.add(item);
      });
  }

  /** Load persisted items for the current user from localStorage. */
  loadItems(): void {
    const items = this.read().map(i => this.migrate(i));
    this._items$.next(items);
    this.fetchMissingIcons(items);
  }

  /** Back-fill missing fields (colorKey, iconUrl) on older persisted items. */
  private migrate(item: RecentlyViewedItem): RecentlyViewedItem {
    let patched = item;

    if (!patched.colorKey) {
      const found = STATIC_ROUTES.find(r => r.pattern.test(patched.url));
      patched = { ...patched, colorKey: found?.item.colorKey ?? (patched.type === 'client' ? 'blue' : 'slate') };
    }

    // Resolve client icon for client-type items that don't have one yet
    if (patched.type === 'client' && !patched.iconUrl) {
      const clientMatch = patched.url.match(/^\/client\/(.+)$/) ?? patched.url.match(/^\/data-explorer\/([^/]+)/) ?? patched.url.match(/^\/prod-data\/([^/]+)/);
      if (clientMatch) {
        const iconUrl = this.resolveClientIcon(clientMatch[1]);
        if (iconUrl) patched = { ...patched, iconUrl };
      }
    }

    return patched;
  }

  /** Fetch icons from the API for client items that couldn't be resolved from cache. */
  private fetchMissingIcons(items: RecentlyViewedItem[]): void {
    const clientItems = items.filter(i => i.type === 'client' && !i.iconUrl);
    if (clientItems.length === 0) return;

    const fetched = new Set<string>();
    for (const item of clientItems) {
      const match = item.url.match(/^\/client\/(.+)$/) ?? item.url.match(/^\/data-explorer\/([^/]+)/) ?? item.url.match(/^\/prod-data\/([^/]+)/);
      if (!match || fetched.has(match[1])) continue;
      const clientId = match[1];
      fetched.add(clientId);

      this.http.get<any>(`ClientSettings/GetIcon?id=${clientId}`, (result: any) => {
        if (!result?.iconText) return;
        const current = this._items$.value.map(i => {
          const m = i.url.match(/^\/client\/(.+)$/) ?? i.url.match(/^\/data-explorer\/([^/]+)/) ?? i.url.match(/^\/prod-data\/([^/]+)/);
          if (m && m[1] === clientId && !i.iconUrl) {
            return { ...i, iconUrl: result.iconText };
          }
          return i;
        });
        this._items$.next(current);
        this.write(current);
      }, false);
    }
  }

  /** Manually push an item (e.g., client detail with a resolved name). */
  add(item: RecentlyViewedItem): void {
    const existing = this.read();
    // Deduplicate by URL — remove old entry if present, then prepend new
    const deduped = existing.filter(i => i.url !== item.url);
    const updated = [{ ...item, timestamp: Date.now() }, ...deduped].slice(0, this.MAX_ITEMS);
    this.write(updated);
    this._items$.next(updated);

    // If this is a client item without an icon, try to fetch it
    if (item.type === 'client' && !item.iconUrl) {
      this.fetchMissingIcons(updated);
    }
  }

  /** Clear all recently viewed items for the current user. */
  clear(): void {
    localStorage.removeItem(this.storageKey);
    this._items$.next([]);
  }

  /** Synchronous snapshot (use items$ for reactive consumers). */
  get items(): RecentlyViewedItem[] {
    return this._items$.value;
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private get storageKey(): string {
    const id = this.loginService.userOptional?.userName ?? 'anonymous';
    return `${this.KEY_PREFIX}${id}`;
  }

  private read(): RecentlyViewedItem[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as RecentlyViewedItem[]) : [];
    } catch {
      return [];
    }
  }

  private write(items: RecentlyViewedItem[]): void {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  private mapUrlToItem(url: string): RecentlyViewedItem | null {
    // Strip query params / fragments before matching
    const path = url.split('?')[0].split('#')[0];

    // Exclude non-content routes
    if (['/home', '/', '/login-failed', '/login', '/logout', '/register'].some(r => path === r)) {
      return null;
    }

    // Client detail pages: /client/{id}
    const clientMatch = path.match(/^\/client\/(.+)$/);
    if (clientMatch) {
      const name = this.resolveClientName(clientMatch[1]);
      const iconUrl = this.resolveClientIcon(clientMatch[1]);
      if (name) {
        return { label: name, url: path, icon: 'building', type: 'client', colorKey: 'blue', iconUrl, subtitle: 'Client', timestamp: Date.now() };
      }
      return { label: 'Client Detail', url: path, icon: 'building', type: 'client', colorKey: 'blue', iconUrl, subtitle: 'Client', timestamp: Date.now() };
    }

    // Data-explorer pages: /data-explorer/{clientId}/{schema}/{table}/{recordId}
    // Keep full URL (with query params like dataSetId) so navigation works correctly
    const explorerMatch = path.match(/^\/data-explorer\/([^/]+)/);
    if (explorerMatch) {
      const name = this.resolveClientName(explorerMatch[1]);
      const iconUrl = this.resolveClientIcon(explorerMatch[1]);
      const fullUrl = url.split('#')[0]; // keep query params, strip fragment only
      return { label: name ?? 'Record Viewer', url: fullUrl, icon: 'speedometer2', type: 'client', colorKey: 'blue', iconUrl, subtitle: 'Record Viewer', timestamp: Date.now() };
    }

    // Static route table
    for (const { pattern, item } of STATIC_ROUTES) {
      if (pattern.test(path)) {
        return { ...item, timestamp: Date.now() };
      }
    }

    return null;
  }

  /** Try to pull the client name from the localStorage cache written by ClientComponent. */
  private resolveClientName(id: string): string | null {
    try {
      const raw = localStorage.getItem(`client-${id}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.name ?? parsed?.clientName ?? parsed?.displayName ?? null;
    } catch {
      return null;
    }
  }

  /** Try to pull the client icon as a data URL from the localStorage cache. */
  private resolveClientIcon(id: string): string | undefined {
    try {
      // First check if this is the current user's client — use their pre-built iconText
      const user = this.loginService.userOptional;
      if (user?.selectedClientId === id && user?.iconText) {
        return user.iconText;
      }
      // Fall back to the cached client object from the client grid
      const raw = localStorage.getItem(`client-${id}`);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      if (parsed?.icon) {
        const mimeType = parsed.iconType || 'image/png';
        return `data:${mimeType};base64,${parsed.icon}`;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }
}
